<template>
  <div 
    ref="container" 
    :class="className" 
    class="datepicker">
    <div class="btn-group summary-group">
      <button 
        v-show="endDate != null && startDate != null"
        class="btn white prev" 
        @click="previousRange()"/>
      <div class="summary">
        <button 
          class="btn white" 
          @click="popup()">{{ rangeText }}</button>
      </div>
      <button 
        v-show="endDate != null && startDate != null"
        class="btn white next"
        @click="nextRange()"/>
    </div>
    <div 
      v-if="showPopup" 
      class="datepicker-popup">
      <div class="datepicker-inner">
        <div class="datepicker-body">
          <div class="datepicker-ctrl">
            <span 
              v-show="canGoPrevious" 
              class="datepicker-preBtn glyphicon glyphicon-chevron-left"
              aria-hidden="true" 
              @click="previous()"/>
            <span 
              v-show="canGoNext" 
              class="datepicker-nextBtn glyphicon glyphicon-chevron-right"
              aria-hidden="true" 
              @click="next()"/>
            <p @click="selectMonth(currentDate)">{{ currentDate.format('MMMM YYYY') }}</p>
          </div>
          <div class="datepicker-weekRange">
            <span 
              v-for="(w, index) in daysOfWeek" 
              :key="index">{{ w }}</span>
          </div>
          <div class="datepicker-dateRange">
            <span 
              v-for="(d, index) in dateRange" 
              :key="index" 
              :class="d.sclass" 
              @click="selectDate(d.date)">{{ d.text }}</span>
          </div>
          <div class="datepicker-rangeInput">
            <div class="datepicker-startInput">
              <small class="text-muted">Start</small>
              <input 
                :value="startDateText"
                class="form-control"
                type="text"
                @blur="setStartDate($event.target.value)"
                @keyup.enter="setStartDate($event.target.value)">
            </div>
            <div class="datepicker-endInput">
              <small class="text-muted">End</small>
              <input 
                :value="endDateText"
                class="form-control"
                type="text"
                @blur="setEndDate($event.target.value)"
                @keyup.enter="setEndDate($event.target.value)">
            </div>
          </div>
        </div>
        <div class="datepicker-shortcuts">
          <div class="btn-group-vertical">
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectToday()">Today</button>
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectYesterday()">Yesterday</button>
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectThisWeek()">This Week</button>
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectLastWeek()">Last Week</button>
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectThisMonth()">This Month</button>
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectLastMonth()">Last Month</button>
            <button 
              v-if="allowEmptyDate" 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="clearSelectedDate()">Clear</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import m from 'moment';
import range from 'moment-range';
import twix from 'twix';
import { mapMutations } from 'vuex';

export default {
    props: {
        format: {
            type: String,
            default: 'YYYY-MM-DD',
        },
        min: {
            type: Object,
            default: () => m('2014-01-01'),
        },
        max: {
            type: Object,
            default: () => m().endOf('day'),
        },
        start: {
            type: Object,
            default: () => null,
        },
        end: {
            type: Object,
            default: () => null,
        },
        useVuex: {
            type: Boolean,
            default: false,
        },
        className: {
            type: String,
            default: '',
        },
        allowEmptyDate: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            currentDate: m(),
            startDate: this.allowEmptyDate ? null : m().startOf('day'),
            endDate: this.allowEmptyDate ? null : m().endOf('day'),
            daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
            dateRange: [],
            hasStart: true,
            hasEnd: true,
            showPopup: false,
        };
    },

    computed: {
        canGoPrevious() {
            let newDate = m(this.currentDate).subtract(1, 'month');
            return newDate.isSameOrAfter(
                this.getMinimumDate().startOf('month'),
            );
        },

        canGoNext() {
            let newDate = m(this.currentDate).add(1, 'month');
            return newDate.isSameOrBefore(this.getMaximumDate().endOf('month'));
        },

        startDateText() {
            return this.hasStartDate
                ? this.getStartDate().format('YYYY-MM-DD hh:mm A')
                : null;
        },

        endDateText() {
            return this.hasEndDate
                ? this.getEndDate().format('YYYY-MM-DD hh:mm A')
                : null;
        },

        rangeText() {
            if (this.hasNoStartAndEndDate) {
                return 'Filter Date';
            }

            return m(this.getStartDate())
                .twix(this.getEndDate(), {
                    allDay: true,
                })
                .format();
        },

        hasStartDate() {
            return this.getStartDate() !== null;
        },

        hasEndDate() {
            return this.getEndDate() !== null;
        },

        hasNoStartAndEndDate() {
            return !this.hasEndDate && !this.hasStartDate;
        },
    },

    watch: {
        currentDate() {
            this.generateDateRange();
        },

        startDate() {
            this.generateDateRange();
        },

        endDate() {
            this.generateDateRange();
        },
    },

    mounted() {
        document.addEventListener('click', this.handleClickOutside, true);
        this.setStartAndEndDate();
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside, true);
    },

    methods: {
        ...mapMutations(['setDateFilterRange', 'resetDateFilterRange']),

        popup() {
            if (this.showPopup) {
                this.closePopup();
            } else {
                this.openPopup();
            }
        },

        openPopup() {
            this.showPopup = true;
            this.generateDateRange();
        },

        closePopup() {
            this.showPopup = false;
        },

        handleClickOutside(e) {
            const el = this.$refs.container;

            if (!el.contains(e.target)) {
                if (this.showPopup) {
                    this.closePopup();
                }
            }
        },

        generateDateRange() {
            this.dateRange = [];

            var that = this;

            var start = m(this.currentDate)
                .startOf('month')
                .startOf('week');

            var end = m(this.currentDate)
                .endOf('month')
                .endOf('week');

            if (this.extraPreviousWeekEnabled()) {
                start.subtract(1, 'week');
            }

            if (
                !this.extraPreviousWeekEnabled() &&
                this.extraNextWeekEnabled()
            ) {
                end.add(1, 'week');
            }

            var range = m.range(start, end);

            range.by('days', function(moment) {
                that.dateRange.push({
                    date: moment.startOf('day'),
                    text: moment.format('D'),
                    sclass: that.getDateClass(moment),
                });
            });
        },

        clampDate(date) {
            if (date == null) {
                return date;
            }

            if (date.isAfter(this.getMaximumDate())) {
                return this.getMaximumDate();
            }

            if (date.isBefore(this.getMinimumDate())) {
                return this.getMinimumDate();
            }

            return date;
        },

        selectDate(date) {
            if (
                this.getMinimumDate().isAfter(date) ||
                this.getMaximumDate().isBefore(date)
            ) {
                return;
            }

            if (this.hasStart && !this.hasEnd) {
                if (date.isBefore(this.startDate)) {
                    var temp = m(this.startDate);
                    this.selectRange(date, temp);
                    this.hasEnd = true;
                } else {
                    this.selectRange(this.startDate, date);
                    this.hasEnd = true;
                }

                return;
            }

            if (this.hasStart && this.hasEnd) {
                this.selectRange(date, date);
                this.hasEnd = false;
            }
        },

        setStartDate(date, time = true) {
            var finalDate = this.parseDate(date, null);

            if (finalDate != null && time) {
                finalDate = finalDate.startOf('day');
            }

            this.startDate = this.clampDate(finalDate);
        },

        setEndDate(date, time = true) {
            var finalDate = this.parseDate(date, null);

            if (finalDate != null && time) {
                finalDate = finalDate.endOf('day');
            }

            this.endDate = this.clampDate(finalDate);
        },

        parseDate(date, def = m()) {
            if (m.isMoment(date)) {
                return m(date);
            }

            let parsed = m(date, this.format);

            if (parsed.isValid()) {
                return parsed;
            }

            return def;
        },

        navigateToDate(date) {
            let month = date.month();
            let year = date.year();

            this.currentDate = m(this.currentDate)
                .month(month)
                .year(year);
        },

        selectRange(start, end) {
            this.setStartDate(start);
            this.setEndDate(end);
            this.navigateToDate(this.clampDate(start));

            this.$emit('onChange', {
                start: this.getStartDate(),
                end: this.getEndDate(),
            });

            if (this.useVuex) {
                this.$store.commit('setDateFilterRange', {
                    start: this.startDate,
                    end: this.endDate,
                });
            }
        },

        selectToday() {
            var start = m().startOf('day');
            var end = m().endOf('day');
            this.selectRange(start, end);
            this.closePopup();
        },

        selectYesterday() {
            var start = m()
                .subtract(1, 'day')
                .startOf('day');
            var end = m()
                .subtract(1, 'day')
                .endOf('day');
            this.selectRange(start, end);
            this.closePopup();
        },

        selectThisWeek() {
            var start = m()
                .startOf('week')
                .startOf('day');
            var end = m()
                .endOf('week')
                .endOf('day');
            this.selectRange(start, end);
            this.closePopup();
        },

        selectLastWeek() {
            var start = m()
                .startOf('week')
                .subtract(1, 'week')
                .startOf('day');
            var end = m()
                .endOf('week')
                .subtract(1, 'week')
                .endOf('day');
            this.selectRange(start, end);
            this.closePopup();
        },

        selectThisMonth() {
            var start = m()
                .startOf('month')
                .startOf('day');
            var end = m()
                .endOf('month')
                .endOf('day');
            this.selectRange(start, end);
            this.closePopup();
        },

        selectLastMonth() {
            var start = m()
                .startOf('month')
                .subtract(1, 'month')
                .startOf('day');
            var end = m()
                .endOf('month')
                .subtract(1, 'month')
                .endOf('day');
            this.selectRange(start, end);
            this.closePopup();
        },

        selectMonth(date) {
            var start = m(this.currentDate)
                .startOf('month')
                .startOf('day');
            var end = m(this.currentDate)
                .endOf('month')
                .endOf('day');
            this.selectRange(start, end);
            this.closePopup();
        },

        setStartAndEndDate() {
            if (this.start === null && this.end === null) {
                if (!this.allowEmptyDate) {
                    this.selectToday();
                }

                return;
            }

            if (this.start !== null) {
                this.startDate = m(this.start, this.format);
            }

            if (this.end !== null) {
                this.endDate = m(this.end, this.format);
            }
        },

        extraPreviousWeekEnabled() {
            return m(this.currentDate)
                .startOf('month')
                .endOf('week')
                .subtract(4, 'day')
                .isSame(this.currentDate, 'month');
        },

        extraNextWeekEnabled() {
            return m(this.currentDate)
                .endOf('month')
                .startOf('week')
                .add(4, 'day')
                .isSame(this.currentDate, 'month');
        },

        getDateClass(date) {
            var clazz = [];

            if (
                !this.currentDate.isSame(date, 'month') &&
                !this.hasNoStartAndEndDate
            ) {
                clazz.push('datepicker-day-different-month');
            }

            if (m().isSame(date, 'day')) {
                clazz.push('datepicker-day-today');
            }

            if (
                date.isBetween(
                    this.getStartDate(),
                    this.getEndDate(),
                    'day',
                    '[]',
                ) &&
                this.hasStartDate &&
                this.hasEndDate
            ) {
                clazz.push('datepicker-day-selected');
            }

            if (
                this.getMinimumDate().isAfter(date) ||
                this.getMaximumDate().isBefore(date)
            ) {
                clazz.push('datepicker-day-disabled');
            }

            return clazz.join(' ');
        },

        getStartDate() {
            if (this.startDate == null && !this.allowEmptyDate) {
                return m().startOf('day');
            }

            return this.startDate;
        },

        getEndDate() {
            if (this.endDate == null && !this.allowEmptyDate) {
                if (this.startDate != null) {
                    return this.getStartDate().endOf('day');
                } else {
                    return m().endOf('day');
                }
            }

            return this.endDate;
        },

        getMinimumDate() {
            if (this.min != null) {
                return m(this.min, this.format);
            }

            return m('2014-01-01');
        },

        getMaximumDate() {
            if (this.max != null) {
                return m(this.max, this.format);
            }

            return m().endOf('day');
        },

        rangeIsExactlyOneMonth() {
            let lastDay = m(this.getStartDate())
                .endOf('month')
                .date();
            let isFirstDay = m(this.getStartDate()).date() == 1;
            let isLastDay = m(this.getEndDate()).date() == lastDay;

            return isFirstDay && isLastDay;
        },

        previous() {
            if (this.canGoPrevious) {
                this.currentDate = m(this.currentDate).subtract(1, 'month');
            }
        },

        next() {
            if (this.canGoNext) {
                this.currentDate = m(this.currentDate).add(1, 'month');
            }
        },

        previousRange() {
            let start;
            let end;

            if (this.rangeIsExactlyOneMonth()) {
                start = this.getStartDate()
                    .subtract(1, 'month')
                    .startOf('month');

                end = this.getEndDate()
                    .subtract(1, 'month')
                    .endOf('month');
            } else {
                let range =
                    m
                        .range(this.getStartDate(), this.getEndDate())
                        .diff('days') + 1;

                start = m(this.getStartDate()).subtract(range, 'day');

                end = m(this.getEndDate()).subtract(range, 'day');
            }

            this.selectRange(start, end);
        },

        nextRange() {
            let start;
            let end;

            if (this.rangeIsExactlyOneMonth()) {
                start = this.getStartDate()
                    .add(1, 'month')
                    .startOf('month');

                end = this.getEndDate()
                    .add(1, 'month')
                    .endOf('month');
            } else {
                let range =
                    m
                        .range(this.getStartDate(), this.getEndDate())
                        .diff('days') + 1;

                start = m(this.getStartDate()).add(range, 'day');

                end = m(this.getEndDate()).add(range, 'day');
            }

            this.selectRange(start, end);
        },

        clearSelectedDate() {
            this.setStartDate(null);
            this.setEndDate(null);

            this.$emit('onChange', {
                start: this.getStartDate(),
                end: this.getEndDate(),
            });

            if (this.useVuex) {
                this.resetDateFilterRange();
            }
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
@import '../../../sass/mixins';

$border-color: #ccc;

.body {
    display: inline-block;
    width: 28px;
    line-height: 28px;
    height: 28px;
    border-radius: 4px;
    @include user-select(none);
}

.datepicker {
    position: relative;
}

.datepicker-inner {
    width: 350px;

    > div {
        float: left;
    }
}

.datepicker-body {
    width: 218px;
    padding: 10px 10px;
    border-right: 1px solid $border-color;
    display: inline-block;

    span {
        @extend .body;
        text-align: center;
    }
}

.datepicker-shortcuts {
    width: 132px;
    padding: 10px 10px;
    display: inline-block;
    vertical-align: top;
}

.datepicker-dateRange {
    span {
        cursor: pointer;

        &:hover {
            background-color: #eeeeee;
        }
    }
}

.datepicker-weekRange {
    span {
        font-weight: bold;
    }
}

.datepicker-day-disabled {
    color: #ddd !important;
    cursor: not-allowed !important;

    &:hover {
        background-color: transparent !important;
    }
}

.datepicker-day-different-month {
    color: #999;
}

.datepicker-day-today {
    font-weight: bold;
}

.datepicker-day-selected {
    background-color: #0cc2aa;
    border-radius: 0 !important;
    color: #fff;

    &:hover {
        background-color: #0cc2aa !important;
    }
}

.datepicker-ctrl {
    position: relative;
    height: 30px;
    line-height: 30px;
    font-weight: bold;
    text-align: center;

    p {
        @extend .body;
        width: 65%;
        cursor: pointer;

        &:hover {
            background-color: #eeeeee;
        }
    }

    span {
        @extend .body;
        position: absolute;
        cursor: pointer;
    }
}

.datepicker-popup {
    position: absolute;
    right: 0;
    border: 1px solid $border-color;
    border-radius: 5px;
    background: #fff;
    margin-top: 2px;
    z-index: 1000;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
}

.datepicker-rangeInput {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid $border-color;

    div + div {
        margin-top: 10px;
    }

    input {
        font-size: 14px;
    }
}

.datepicker-preBtn,
.datepicker-nextBtn {
    &:hover {
        background-color: #eeeeee;
    }
}

.datepicker-preBtn {
    left: 2px;
}

.datepicker-nextBtn {
    right: 2px;
}

.summary-group {
    width: 100%;
    display: flex;

    .chevron {
        display: inline-block;
        width: 0;
        height: 0;
        margin-right: 0.25rem;
        margin-left: 0.25rem;
        vertical-align: middle;
        content: '';
    }

    .prev {
        float: left;
        margin-right: -1px;

        &::after {
            @extend .chevron;
            border-top: 0.3em solid transparent;
            border-right: 0.3em solid;
            border-bottom: 0.3em solid transparent;
        }
    }

    .summary {
        overflow: hidden;
        flex-grow: 1;
        margin-left: -1px;
        margin-right: -1px;

        .btn {
            width: 100%;
            -webkit-border-radius: 0;
            -moz-border-radius: 0;
            border-radius: 0;

            &::after {
                @extend .chevron;
                border-top: 0.3em solid;
                border-right: 0.3em solid transparent;
                border-left: 0.3em solid transparent;
            }
        }
    }

    .next {
        float: right;

        &::after {
            @extend .chevron;
            border-top: 0.3em solid transparent;
            border-left: 0.3em solid;
            border-bottom: 0.3em solid transparent;
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/vendor/date-picker.vue